import React, { ReactNode, useState, createContext, useContext } from 'react';
import { Header, type UserInfo, type NavItem as HeaderNavItem } from '../../organisms/Header';
import { FocusPanel, type NavItem as FocusPanelNavItem, defaultNavItems } from '../../organisms/FocusPanel';
import './MainLayout.css';

export type AppSection = 'pruefstatus' | 'workspace' | 'my-farm' | 'documents' | 'history' | 'admin' | 'apps' | 'agent';

// Context for sharing mobile side panel state with nested components (e.g., DocumentsPageLayout)
interface MobileSidePanelContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const MobileSidePanelContext = createContext<MobileSidePanelContextValue | null>(null);

/**
 * Hook to access mobile side panel state from within MainLayout.
 * Returns null if not inside a MainLayout.
 */
export function useMobileSidePanel(): MobileSidePanelContextValue | null {
  return useContext(MobileSidePanelContext);
}

export interface MainLayoutProps {
  /** Currently active section */
  activeSection: AppSection;
  /** Handler for section change from FocusPanel */
  onSectionChange: (section: AppSection) => void;
  /** User information for header */
  user?: UserInfo;
  /** Logo image source */
  logoSrc?: string;
  /** Logo link target (defaults to '/') */
  logoHref?: string;
  /** Brand name */
  brandName?: string;
  /** Header navigation items (optional, hidden when showSearch is true) */
  headerNavItems?: HeaderNavItem[];
  /** Focus panel navigation items (defaults to standard app sections) */
  focusPanelNavItems?: FocusPanelNavItem[];
  /** Whether to show search in header */
  showSearch?: boolean;
  /** Current search query */
  searchQuery?: string;
  /** Search query change handler */
  onSearchChange?: (query: string) => void;
  /** Search placeholder text (shown when expanded) */
  searchPlaceholder?: string;
  /** Search collapsed label (shown when collapsed) */
  searchCollapsedLabel?: string;
  /** Handler for logout */
  onLogout?: () => void;
  /** Handler for profile click */
  onProfileClick?: () => void;
  /** Handler for settings click */
  onSettingsClick?: () => void;
  /** Handler for integrations click */
  onIntegrationsClick?: () => void;
  /** Whether focus panel starts expanded */
  focusPanelExpanded?: boolean;
  /** Optional class name */
  className?: string;
  /** Page content */
  children: ReactNode;
  /** Optional footer element */
  footer?: ReactNode;
  /** Label for profile menu item */
  profileLabel?: string;
  /** Label for settings menu item */
  settingsLabel?: string;
  /** Label for integrations menu item */
  integrationsLabel?: string;
  /** Label for sign out menu item */
  signOutLabel?: string;
  /** Mobile: handler for side panel menu button (opens LensePanel) */
  onMobileSideMenuClick?: () => void;
  /** Mobile: page title to show in header */
  mobilePageTitle?: string;
}

/**
 * MainLayout - The top-level application layout template.
 * Combines Header with CollapsibleSearch and FocusPanel for app navigation.
 * Section-specific page layouts are rendered as children.
 */
export const MainLayout: React.FC<MainLayoutProps> = ({
  activeSection,
  onSectionChange,
  user,
  logoSrc,
  logoHref,
  brandName = 'Hofsync',
  headerNavItems = [],
  focusPanelNavItems = defaultNavItems,
  showSearch = true,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchCollapsedLabel = 'Search...',
  onLogout,
  onProfileClick,
  onSettingsClick,
  onIntegrationsClick,
  focusPanelExpanded = true,
  className = '',
  children,
  footer,
  profileLabel,
  settingsLabel,
  integrationsLabel,
  signOutLabel,
  onMobileSideMenuClick,
  mobilePageTitle,
}) => {
  // Internal state for mobile side panel (used by nested components like DocumentsPageLayout)
  const [mobileSidePanelOpen, setMobileSidePanelOpen] = useState(false);

  const mobileSidePanelContextValue: MobileSidePanelContextValue = {
    isOpen: mobileSidePanelOpen,
    open: () => setMobileSidePanelOpen(true),
    close: () => setMobileSidePanelOpen(false),
  };

  const handleSectionChange = (sectionId: string) => {
    onSectionChange(sectionId as AppSection);
  };

  // Convert FocusPanel items to Header nav items for mobile menu
  const mobileNavItems: HeaderNavItem[] = focusPanelNavItems.map((item) => ({
    label: item.name,
    href: '#',
    onClick: () => onSectionChange(item.id as AppSection),
  }));

  // Use external handler if provided, otherwise use internal state toggle
  const handleMobileSideMenuClick = onMobileSideMenuClick ?? (() => setMobileSidePanelOpen(true));

  return (
    <MobileSidePanelContext.Provider value={mobileSidePanelContextValue}>
      <div className={`main-layout ${className}`}>
        {/* Fixed Header */}
        <Header
          logoSrc={logoSrc}
          logoHref={logoHref}
          brandName={brandName}
          navItems={headerNavItems.length > 0 ? headerNavItems : mobileNavItems}
          user={user}
          onLogout={onLogout}
          onProfileClick={onProfileClick}
          onSettingsClick={onSettingsClick}
          onIntegrationsClick={onIntegrationsClick}
          showSearch={showSearch}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          searchCollapsedLabel={searchCollapsedLabel}
          profileLabel={profileLabel}
          settingsLabel={settingsLabel}
          integrationsLabel={integrationsLabel}
          signOutLabel={signOutLabel}
          onMobileSideMenuClick={handleMobileSideMenuClick}
          mobilePageTitle={mobilePageTitle}
          compact
          className="main-layout__header"
        />

      {/* Main content area with FocusPanel and page content */}
      <div className="main-layout__body">
        {/* Focus navigation panel */}
        <FocusPanel
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          navItems={focusPanelNavItems}
          defaultExpanded={focusPanelExpanded}
          className="main-layout__focus-panel"
        />

        {/* Page content area */}
        <main className="main-layout__content">
          {children}
        </main>
      </div>

      {/* Footer spans full width, above left panel */}
      {footer && (
        <div className="main-layout__footer">
          {footer}
        </div>
      )}
    </div>
  </MobileSidePanelContext.Provider>
  );
};

export default MainLayout;
